import { useEffect, useState, type MouseEvent } from 'react'
import { Milestone, AlertTriangle, Building2, ChevronDown, PencilLine, Plus, Presentation, Search, Ticket, X } from 'lucide-react'
import { Link, useNavigate } from '@tanstack/react-router'
import { formatDate, formatDayLabel } from '@/lib/dates'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { EmptyState } from '@/components/ui/empty-state'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ListSkeleton } from '@/components/ui/skeleton'
import { useTrackerCompanies, useTrackerTickets } from '@/queries/tracker'
import { useIsAdmin } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { formatTicketRef } from '@/lib/ticketRef'
import { widerThanShell } from '@/lib/layout'
import { CreateTrackedItemDialog } from './CreateTrackedItemDialog'
import { WaitingOnPill } from './WaitingOnPill'
import {
  trackerStatusLabels,
  trackerStatusOrder,
  trackerStatusVariants,
  devStageLabels,
  devStageVariants,
  priorityLabels,
} from '@/lib/status'
import type { TrackerClosedScope, TrackerStatus, TrackerTicket, TrackerWaitingOn } from '@/types/tracker'
import type { TicketPriority } from '@/types/ticket'

const CLOSED_STATUSES: TrackerStatus[] = ['closed', 'wont_do']
const OPEN_STATUSES = trackerStatusOrder.filter(status => !CLOSED_STATUSES.includes(status))

const TABS: Array<{ label: string; closed: TrackerClosedScope; statuses: TrackerStatus[]; allLabel: string }> = [
  { label: 'Open', closed: 'exclude', statuses: OPEN_STATUSES, allLabel: 'All open statuses' },
  { label: 'Closed', closed: 'only', statuses: CLOSED_STATUSES, allLabel: 'All closed statuses' },
  { label: 'All', closed: 'include', statuses: trackerStatusOrder, allLabel: 'All statuses' },
]

const PRIORITIES: TicketPriority[] = ['high', 'normal', 'low']

type PresentSearch = { closed?: TrackerClosedScope; status?: string; priority?: string; q?: string; waitingOn?: string }

type Origin = 'all' | 'help-desk' | 'manual'
const originOf = (ticket: TrackerTicket): Exclude<Origin, 'all'> => (ticket.context?.source === 'staff' ? 'manual' : 'help-desk')
const linearIdentifier = (url?: string | null) => url?.match(/\/issue\/([A-Z]+-\d+)/)?.[1] ?? null

// V2 filterbar select (mockup `.filterbar select`): pill radius, 36px,
// hairline border — shared by every select in this filterbar.
const filterSelectClass =
  'h-[var(--etn-control-height-input)] shrink-0 rounded-etn-pill border border-etn-border-input bg-etn-surface px-4 text-etn-control text-etn-text-cell'

// V2 table header cell (mockup `th`): 36px, 11px uppercase letterspaced label.
const thClass =
  'h-9 whitespace-nowrap border-b border-[var(--etn-gray-100)] px-4 text-left text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'

// V2 table card. overflow-x-auto rather than overflow-hidden: still clips to
// the rounded corners, but a narrow viewport scrolls a wide table instead of
// silently losing its right-hand columns.
const tableCardClass =
  'overflow-x-auto rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper'

// Seven columns don't fit AppShell's 1080px column without cutting the subject and next action short, so the page
// takes up to 1400px, and those two wrap rather than truncate: the tracker is read on a shared screen, where a
// hover title can't help.
const WIDE_PAGE = widerThanShell(1400, '4rem')

function TrackerRow({
  ticket,
  presentSearch,
  showPresentAction,
  showOrigin = false,
}: {
  showOrigin?: boolean
  ticket: TrackerTicket
  /** The list's current filters, carried into the Present link — see TrackerListView. */
  presentSearch: PresentSearch
  /** Whether the CURRENT VIEWER is staff — independent of the list's own admin mode, since a staffer browsing their own /tracker still gets the affordance. Decided once by the list so every row in a render agrees. */
  showPresentAction: boolean
}) {
  const navigate = useNavigate()
  const { tracker } = ticket
  const to = `/tracker/${ticket._id}`

  const goToTicket = (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.target instanceof Element && e.target.closest('a, button')) return
    navigate({ to: to as never })
  }

  return (
    <tr onClick={goToTicket} className="group cursor-pointer text-etn-cell transition-colors hover:bg-etn-row-hover">
      <td className="h-11 min-w-[300px] px-4 py-2.5 align-middle font-medium text-etn-text">
        <Link to={to as never} className="hover:underline">
          {ticket.subject}
        </Link>
        <small className="ml-2 whitespace-nowrap font-normal text-etn-micro text-etn-text-soft">{formatTicketRef(ticket)}</small>
        {showPresentAction && tracker.newReply && <Badge variant="warning" className="ml-2 whitespace-nowrap">New reply</Badge>}
        <div className="mt-0.5 text-[11.5px] font-normal text-etn-text-soft">
          {[ticket.creator?.name, formatDayLabel(ticket.createdAt)].filter(Boolean).join(' · ')}
        </div>
      </td>
      {showOrigin && (
        <td className="h-11 whitespace-nowrap px-4 py-2.5 align-middle font-medium text-etn-accent-text-active">
          <span className="inline-flex items-center gap-1.5">
            {originOf(ticket) === 'manual' ? <PencilLine className="h-3.5 w-3.5" /> : <Ticket className="h-3.5 w-3.5" />}
            {originOf(ticket) === 'manual' ? 'Added manually' : 'Help desk ticket'}
          </span>
        </td>
      )}
      <td className="h-11 whitespace-nowrap px-4 py-2.5 align-middle font-normal text-etn-text-cell">{tracker.category ?? ''}</td>
      <td className="h-11 px-4 py-2.5 align-middle">
        <Badge variant={trackerStatusVariants[tracker.status]}>{trackerStatusLabels[tracker.status]}</Badge>
      </td>
      <td className="h-11 px-4 py-2.5 align-middle">
        {tracker.waitingOn ? (
          <WaitingOnPill waitingOn={tracker.waitingOn} companyName={ticket.companyName} />
        ) : (
          <span className="font-normal text-etn-text-soft">-</span>
        )}
      </td>
      <td className="h-11 min-w-[200px] px-4 py-2.5 align-middle font-normal text-etn-text-cell">
        {tracker.nextAction || <span className="text-etn-text-soft">-</span>}
      </td>
      <td className="h-11 whitespace-nowrap px-4 py-2.5 align-middle">
        <Badge variant={devStageVariants[tracker.devStage]}>{devStageLabels[tracker.devStage]}</Badge>
        {(linearIdentifier(ticket.linearIssueUrl) || tracker.expectedDate) && (
          <div className="mt-0.5 text-[11.5px] font-normal text-etn-text-soft">
            {[linearIdentifier(ticket.linearIssueUrl), tracker.expectedDate && `Expected ${formatDate(tracker.expectedDate.slice(0, 10))}`]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
      </td>
      {showPresentAction && (
        <td className="h-11 px-4 align-middle text-right">
          <Link
            to="/tracker/present"
            search={{ companyId: ticket.companyId, ...presentSearch } as never}
            aria-label="Present"
            title="Present"
            className="inline-flex rounded-etn-menu p-1.5 text-etn-text-soft opacity-0 transition-opacity hover:bg-etn-control-subtle hover:text-etn-text group-hover:opacity-100 focus-visible:opacity-100"
          >
            <Presentation className="h-4 w-4" />
          </Link>
        </td>
      )}
    </tr>
  )
}

function MultiSelectFilter<T extends string>({
  label,
  allLabel,
  options,
  selected,
  onChange,
}: {
  label: string
  allLabel: string
  options: Array<{ value: T; label: string }>
  selected: T[]
  onChange: (values: T[]) => void
}) {
  const toggle = (value: T) => onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])
  const summary = selected.length ? options.filter(o => selected.includes(o.value)).map(o => o.label).join(', ') : allLabel
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={label} className={`${filterSelectClass} inline-flex max-w-64 items-center gap-2`}>
          <span className="truncate">{summary}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-etn-text-soft" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-1.5">
        {options.map(option => (
          <label key={option.value} className="flex cursor-pointer items-center gap-2.5 rounded-etn-menu px-2.5 py-1.5 text-etn-control hover:bg-etn-control-subtle">
            <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)} />
            {option.label}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  )
}

function emptyStateCopy(closed: TrackerClosedScope, hasFilters: boolean, trackerEmpty: boolean) {
  if (hasFilters) return { title: 'Nothing matches these filters', body: 'Try a different search or clear the filters.' }
  if (trackerEmpty || closed === 'include') {
    return { title: 'No promoted tickets yet', body: "When we promote a ticket to the tracker it'll show up here." }
  }
  if (closed === 'only') return { title: 'Nothing closed yet', body: "Closed and won't do items will show here." }
  return { title: 'Nothing open right now', body: 'Every tracked item is closed. They are on the Closed tab.' }
}

export function TrackerListView({
  asUser,
  admin = false,
  companyId = '',
  onCompanyChange,
  showOrigin = false,
  embedded = false,
}: {
  asUser?: string
  admin?: boolean
  /** Adds an Origin column: a promoted help desk ticket or an item added manually. */
  showOrigin?: boolean
  /** Shown inside the client dashboard, which supplies the page heading, width and company. */
  embedded?: boolean
  /** Staff's chosen company, kept in the route's URL. */
  companyId?: string
  onCompanyChange?: (companyId: string) => void
}) {
  const [tab, setTab] = useState(TABS[0])
  const [statuses, setStatuses] = useState<TrackerStatus[]>([])
  const [priorities, setPriorities] = useState<TicketPriority[]>([])
  const [waitingOns, setWaitingOns] = useState<TrackerWaitingOn[]>([])
  const [origin, setOrigin] = useState<Origin>('all')

  // Staff-only, like TrackerControls' "Track this issue" — decided once here
  // (not per row) so the Actions column header and every row's cell agree on
  // whether it exists. Independent of `admin` (the list's own cross-company
  // mode): a staffer browsing their own /tracker still gets the affordance.
  const isAdminViewer = useIsAdmin()
  // Staff see one company at a time and nothing until they pick one: the tracker is shared on screen with clients, so
  // it must never show another client's issues. The API refuses a staff list without a company too.
  const staffView = admin || (isAdminViewer && !asUser)
  const needsCompany = staffView && !companyId

  // Debounced search, same shape as AdminQueueView's: local input state, and
  // an applied value that drives both the request and the Present link so
  // the two never disagree about what's actually been searched for.
  const [searchText, setSearchText] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQ(searchText.trim()), 300)
    return () => clearTimeout(t)
  }, [searchText])

  const companyParams: Record<string, unknown> = staffView && companyId ? { companyId } : {}
  const filters: PresentSearch = {}
  if (statuses.length) filters.status = statuses.join(',')
  if (priorities.length) filters.priority = priorities.join(',')
  if (waitingOns.length) filters.waitingOn = waitingOns.join(',')
  if (q) filters.q = q
  const params: Record<string, unknown> = { closed: tab.closed, ...filters }
  if (staffView) {
    // Staff ask for the server's max page: an imported tracker can run past the 25-row default.
    params.limit = 100
    Object.assign(params, companyParams)
  }

  const openCount = useTrackerTickets({ ...companyParams, closed: TABS[0].closed, limit: 1 }, asUser, { enabled: !needsCompany })
  const closedCount = useTrackerTickets({ ...companyParams, closed: TABS[1].closed, limit: 1 }, asUser, { enabled: !needsCompany })
  const allCount = useTrackerTickets({ ...companyParams, closed: TABS[2].closed, limit: 1 }, asUser, { enabled: !needsCompany })
  const tabCounts = [openCount, closedCount, allCount].map(count => count.data?.total)
  const { data, isLoading, error, refetch } = useTrackerTickets(params, asUser, { enabled: !needsCompany })
  // Hold an empty list on the skeleton until the All count says whether the tracker has anything in it at all.
  const showSkeleton = isLoading || (!error && (data?.data.length ?? 0) === 0 && allCount.isLoading)
  const hasFilters = statuses.length > 0 || priorities.length > 0 || waitingOns.length > 0 || Boolean(searchText.trim())
  const clearFilters = () => {
    setStatuses([])
    setPriorities([])
    setWaitingOns([])
    setSearchText('')
    setQ('')
  }
  const companies = useTrackerCompanies(staffView)

  const allRows = data?.data ?? []
  const rows = allRows.filter(ticket => origin === 'all' || originOf(ticket) === origin)
  const originPills: { key: Origin; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: allRows.length },
    { key: 'help-desk', label: 'From a help desk ticket', count: allRows.filter(ticket => originOf(ticket) === 'help-desk').length },
    { key: 'manual', label: 'Added manually', count: allRows.filter(ticket => originOf(ticket) === 'manual').length },
  ]
  // True once the server-side page itself is short of the real total.
  const truncated = staffView && (data?.total ?? 0) > allRows.length

  // Carried into each row's Present link (design doc "Presenting follows the
  // list's filters", 2026-08-24) — built once here, not per-row, so every
  // row's deck link agrees on exactly what's currently applied to the list.
  const presentSearch: PresentSearch = tab.closed === 'exclude' ? filters : { closed: tab.closed, ...filters }

  return (
    <>
      <div className="flex gap-8" style={embedded ? undefined : WIDE_PAGE}>
        <div className="flex-1 min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            {embedded ? (
              <div className="flex flex-wrap items-center gap-2">
                {showOrigin &&
                  originPills.map(pill => (
                    <button
                      key={pill.key}
                      type="button"
                      onClick={() => setOrigin(pill.key)}
                      className={cn(
                        'h-8 rounded-etn-pill border px-3 text-[13px] font-medium',
                        origin === pill.key
                          ? 'border-transparent bg-etn-accent-tint font-semibold text-etn-accent-text-active'
                          : 'border-etn-border-input bg-etn-surface text-etn-text'
                      )}
                    >
                      {pill.label} {pill.count}
                    </button>
                  ))}
              </div>
            ) : (
              <h1 className="text-lg font-semibold text-etn-text">Tracker</h1>
            )}
            <div className="flex items-center gap-2">
              {embedded && isAdminViewer && companyId && (
                <Button size="sm" variant="outline" asChild>
                  <Link to="/tracker/present" search={{ companyId, ...presentSearch } as never}>
                    <Presentation className="h-4 w-4" />
                    Present
                  </Link>
                </Button>
              )}
            {/* Not while lensed: the lens shows what the client sees, and they can't add items. */}
            {isAdminViewer && !asUser && (
              <CreateTrackedItemDialog
                initialCompany={companies.data?.data.find(c => c._id === companyId)}
                trigger={
                  <Button size="sm">
                    <Plus className="h-4 w-4" />
                    New tracked item
                  </Button>
                }
              />
            )}
            </div>
          </div>

          {staffView && !embedded && (
            <div className="relative mb-5 w-full max-w-md">
              <Building2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-etn-text-soft" />
              <select
                aria-label="Company"
                className="h-11 w-full appearance-none rounded-etn-pill border border-etn-border-input bg-etn-surface pl-11 pr-10 text-[15px] font-semibold text-etn-text"
                value={companyId}
                onChange={e => onCompanyChange?.(e.target.value)}
              >
                <option value="">Select a company…</option>
                {companies.data?.data.map(c => (
                  <option key={c._id} value={c._id}>{c.name ?? c._id}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-etn-text-soft" />
            </div>
          )}

          <Tabs
            className="mb-4"
            value={tab.label}
            onValueChange={label => {
              setTab(TABS.find(t => t.label === label) ?? TABS[0])
              setStatuses([])
            }}
          >
            <TabsList aria-label="Tracked items">
              {TABS.map((t, index) => (
                <TabsTrigger key={t.label} value={t.label}>
                  {t.label}
                  {tabCounts[index] !== undefined && (
                    <span className="tabular-nums text-etn-micro font-normal text-etn-text-soft">{tabCounts[index]}</span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="mb-5 flex flex-wrap items-center gap-2.5">
            <div className="relative min-w-[200px] max-w-[320px] flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-etn-text-soft" />
              <Input
                pill
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search subjects…"
                aria-label="Search tracked issues"
                className="pl-9"
              />
            </div>
            <MultiSelectFilter
              label="Status"
              allLabel={tab.allLabel}
              options={tab.statuses.map(value => ({ value, label: trackerStatusLabels[value] }))}
              selected={statuses}
              onChange={setStatuses}
            />
            <MultiSelectFilter
              label="Priority"
              allLabel="All priorities"
              options={PRIORITIES.map(value => ({ value, label: priorityLabels[value] }))}
              selected={priorities}
              onChange={setPriorities}
            />
            <MultiSelectFilter
              label="Waiting on"
              allLabel="Waiting on: any"
              options={[
                { value: 'etainabl', label: 'Waiting on Etainabl' },
                { value: 'client', label: isAdminViewer ? 'Waiting on the client' : 'Waiting on your team' },
              ]}
              selected={waitingOns}
              onChange={setWaitingOns}
            />
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-etn-micro text-etn-text-soft hover:text-etn-text"
              >
                <X className="h-3 w-3" /> Clear filters
              </button>
            )}
            <span className="ml-auto flex items-center gap-1.5 text-etn-micro text-etn-text-muted">
              <span aria-hidden className="text-etn-accent-text-active">●</span> Live
            </span>
          </div>

          {needsCompany && companies.error && (
            <div className="flex flex-col items-center gap-3 py-16">
              <EmptyState
                icon={<AlertTriangle />}
                title="Couldn't load the companies."
                body="The company list didn't load, so there's nothing to pick yet. Please try again."
              />
              <Button size="sm" variant="outline" onClick={() => companies.refetch()}>Retry</Button>
            </div>
          )}

          {needsCompany && !companies.error && (
            <EmptyState
              icon={<Building2 />}
              title="Choose a company"
              body="Its tracked items will appear here."
            />
          )}

          {showSkeleton && <ListSkeleton rows={5} />}

          {error && (
            <div className="flex flex-col items-center gap-3 py-16">
              <EmptyState
                icon={<AlertTriangle />}
                title="Couldn't load the tracker."
                body="Something went wrong. Please try again."
              />
              <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
          )}

          {!needsCompany && !showSkeleton && !error && rows.length === 0 && (
            <EmptyState icon={<Milestone />} {...emptyStateCopy(tab.closed, hasFilters, tabCounts[2] === 0)} />
          )}

          {!isLoading && !error && rows.length > 0 && (
            <>
              {truncated && (
                <p className="mb-2 text-etn-micro text-etn-text-soft">
                  Showing first {allRows.length} of {data!.total}. Narrow by status or search to see the rest.
                </p>
              )}
              <div className={tableCardClass}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th scope="col" className={thClass}>Issue</th>
                      {showOrigin && <th scope="col" className={thClass}>Origin</th>}
                      <th scope="col" className={thClass}>Category</th>
                      <th scope="col" className={thClass}>Status</th>
                      <th scope="col" className={thClass}>Waiting on</th>
                      <th scope="col" className={thClass}>Next action</th>
                      <th scope="col" className={thClass}>Delivery</th>
                      {isAdminViewer && (
                        <th scope="col" className={thClass}><span className="sr-only">Actions</span></th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-etn-border-row">
                    {rows.map((t: TrackerTicket) => (
                      <TrackerRow
                        key={t._id}
                        ticket={t}
                        presentSearch={presentSearch}
                        showPresentAction={isAdminViewer}
                        showOrigin={showOrigin}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
